/**
 * Control component registry for settings types.
 *
 * Maps setting types to their corresponding React components.
 * Eliminates switch statements and makes control types pluggable.
 */

import type { ComponentType } from "react";
import { MultiSelectControl } from "./MultiSelectControl";
import { SliderControl } from "./SliderControl";
import { ToggleControl } from "./ToggleControl";
import type { ControlValue } from "./BaseControl";

/**
 * Configuration for slider controls (number type).
 */
export interface SliderControlConfig {
  min: number;
  max: number;
  step?: number;
}

/**
 * Configuration for multi-select controls.
 */
export interface MultiSelectControlConfig {
  pack_metadata_endpoint?: string;
  min_selections?: number;
  max_selections?: number;
}

/**
 * Union type for all control configurations.
 */
export type ControlConfig = SliderControlConfig | MultiSelectControlConfig | undefined;

/**
 * Standard props interface for all control components.
 * Uses proper types instead of `any` for type safety.
 */
export interface ControlComponentProps {
  label: string;
  value: ControlValue;
  description?: string;
  error?: string;
  onChange: (value: ControlValue) => void;
  disabled?: boolean;
  config?: ControlConfig;
  gameId?: string;
  // SliderControl-specific props (passed from config)
  min?: number;
  max?: number;
}

/**
 * Control component type.
 */
export type ControlComponent = ComponentType<ControlComponentProps>;

/**
 * Registry mapping setting types to React components.
 */
const controlRegistry: Record<string, ControlComponent> = {
  number: SliderControl as ControlComponent,
  boolean: ToggleControl as ControlComponent,
  multi_select: MultiSelectControl as ControlComponent,
};

/**
 * Get the appropriate control component for a setting type.
 *
 * @param type - Setting type (e.g., 'number', 'boolean', 'multi_select')
 * @returns Control component or null if type not supported
 */
export function getControlForType(type: string): ControlComponent | null {
  return controlRegistry[type] || null;
}

/**
 * Register a custom control component for a setting type.
 *
 * Allows extending the registry with new control types.
 *
 * @param type - Setting type
 * @param component - React component
 */
export function registerControl(
  type: string,
  component: ControlComponent,
): void {
  controlRegistry[type] = component;
}
